import React, { useState } from 'react';

import SectionInfosGenerales from '../sections/SectionInfosGenerales';
import SectionClient from '../sections/SectionClient';
import SectionInfosI2C from '../sections/SectionInfosI2C';
import SectionProjet from '../sections/SectionProjet';
import SectionCahierCharges from '../sections/SectionCahierCharges';
import SectionMethodologie from '../sections/SectionMethodologie';
import SectionDevis from '../sections/SectionDevis';
import SectionPaiement from '../sections/SectionPaiement';
import SectionDevisSpecifique from '../sections/SectionDevisSpecifique';
import SectionSelectionPhasesLivrables from '../sections/SectionSelectionPhasesLivrables';
import Carte from '../ui/Carte';

import { DOCUMENTS } from '../../documents/registreDocuments';
import { construireIdentifiantEtude } from '../../documents/utilitairesReference';

// Mini sommaire pour naviguer dans la page (ancres natives, pas de JS de scroll)
const SECTIONS_SOMMAIRE = [
  { id: 'infos-generales', label: 'Infos générales' },
  { id: 'client', label: 'Client' },
  { id: 'i2c', label: 'i2c' },
  { id: 'projet', label: 'Projet' },
  { id: 'cahier-des-charges', label: 'Cahier des charges' },
  { id: 'methodologie-budget', label: 'Méthodologie & budget' },
  { id: 'paiement', label: 'Paiement' },
  { id: 'devis-specifique', label: 'Devis' },
  { id: 'phases-livrables-realises', label: 'Phases & livrables réalisés' },
  { id: 'generation', label: '⬇️ Générer' },
];

function badgeStatut(dateGeneration) {
  if (dateGeneration) {
    return { texte: 'Générée', bg: 'var(--vert-clair, #e1f5ee)', couleur: 'var(--vert-fonce, #0f6e56)' };
  }
  return { texte: 'Pas encore générée', bg: 'var(--jaune-clair, #faeeda)', couleur: 'var(--jaune-fonce, #854f0b)' };
}

function VuePageEtude({ etude }) {
  const {
    formData,
    derniereSauvegarde,
    sauvegarderEtude,
    reinitialiserEtude,
    marquerDocumentGenere,
    changerChampDocument,
    changerChampCommun,
    toggleElementDansListeCommun,
    changerInfoGenerale,
    changerClient,
    changerTypePresidence, changerInfoPresident,
    ajouterContact, supprimerContact, modifierContact,
    changerProjet,
    ajouterContrainte, supprimerContrainte, modifierContrainte,
    ajouterExigence, supprimerExigence, modifierExigence,
    ajouterLivrable, supprimerLivrable, modifierLivrable,
    ajouterPhase, supprimerPhase, modifierPhase,
    activerPointsControle, ajouterPointControle, supprimerPointControle, modifierPointControle,
    changerMoyensOutils,
    changerFraisDossier,
    changerConditionsPaiement,
  } = etude;

  const [generationEnCoursId, setGenerationEnCoursId] = useState(null);
  const [erreurGeneration, setErreurGeneration] = useState(null);

  const donneesCE = formData.documents.conventionEtude;

  const handleGenerer = async (doc) => {
    setGenerationEnCoursId(doc.id);
    setErreurGeneration(null);
    try {
      const numDoc = formData.commun.prochainNumDoc;
      await doc.generer(formData, numDoc);
      marquerDocumentGenere(doc.id, numDoc);
      sauvegarderEtude();
    } catch (err) {
      console.error(`Erreur génération ${doc.id} :`, err);
      setErreurGeneration(`Erreur lors de la génération de : ${doc.label}`);
      setGenerationEnCoursId(null);
      return;
    }
    setGenerationEnCoursId(null);
  };

  const handleReinitialiser = () => {
    const confirmation = window.confirm(
      "Voulez-vous vraiment vider toute l'étude ? Cette action est irréversible et supprimera également le brouillon enregistré."
    );
    if (!confirmation) return;
    reinitialiserEtude();
  };

  return (
    <div>
      {/* ── Bandeau d'identification + sommaire ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 13, color: 'var(--texte-secondaire)' }}>
            Identifiant de l'étude : <strong>{construireIdentifiantEtude(formData.commun.infosGenerales)}</strong>
            {derniereSauvegarde && (
              <span style={{ marginLeft: 12 }}>
                (dernier enregistrement à {derniereSauvegarde.toLocaleTimeString('fr-FR')})
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn--secondaire btn--sm" onClick={sauvegarderEtude}>
              💾 Sauvegarder
            </button>
            <button className="btn btn--danger btn--sm" onClick={handleReinitialiser}>
              🗑️ Vider toute l'étude
            </button>
          </div>
        </div>

        <nav style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 16 }}>
          {SECTIONS_SOMMAIRE.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              style={{
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 16px',
                borderRadius: 999,
                background: 'var(--fond-carte, #fff)',
                border: '1.5px solid var(--bleu, #1d4ed8)',
                color: 'var(--bleu, #1d4ed8)',
                textDecoration: 'none',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
              }}
            >
              {s.label}
            </a>
          ))}
        </nav>
      </div>

      {/* ── Sections communes ── */}
      <div id="infos-generales"><SectionInfosGenerales donnees={formData.commun.infosGenerales} onChange={changerInfoGenerale} /></div>

      <div id="client"><SectionClient donnees={formData.commun.client} onChange={changerClient} /></div>

      <div id="i2c">
        <SectionInfosI2C
          typePresidence={formData.commun.i2c.typePresidence}
          contacts={formData.commun.i2c.contacts}
          infoPresident1={formData.commun.i2c.infoPresident1}
          infoPresident2={formData.commun.i2c.infoPresident2}
          onChangerTypePresidence={changerTypePresidence}
          onChangerInfoPresident={changerInfoPresident}
          onAjouterContact={ajouterContact}
          onSupprimerContact={supprimerContact}
          onModifierContact={modifierContact}
        />
      </div>

      {/* ── Sections spécifiques Convention d'Étude ── */}
      <div id="projet"><SectionProjet donnees={donneesCE.projet} onChange={changerProjet} /></div>

      <div id="cahier-des-charges">
        <SectionCahierCharges
          contraintes={donneesCE.contraintes}
          exigences={donneesCE.exigences}
          onAjouterContrainte={ajouterContrainte}
          onSupprimerContrainte={supprimerContrainte}
          onModifierContrainte={modifierContrainte}
          onAjouterExigence={ajouterExigence}
          onSupprimerExigence={supprimerExigence}
          onModifierExigence={modifierExigence}
        />
      </div>

      {/* ── Méthodologie + Budget : communs à la CE ET au Devis ── */}
      <div id="methodologie-budget">
        <SectionMethodologie
          livrables={formData.commun.livrables}
          phases={formData.commun.phases}
          onAjouterLivrable={ajouterLivrable}
          onSupprimerLivrable={supprimerLivrable}
          onModifierLivrable={modifierLivrable}
          onAjouterPhase={ajouterPhase}
          onSupprimerPhase={supprimerPhase}
          onModifierPhase={modifierPhase}
          pointsControleActifs={donneesCE.pointsControleActifs}
          pointsControle={donneesCE.pointsControle}
          onActiverPointsControle={activerPointsControle}
          onAjouterPointControle={ajouterPointControle}
          onSupprimerPointControle={supprimerPointControle}
          onModifierPointControle={modifierPointControle}
          moyensOutils={donneesCE.moyensOutils}
          onChangerMoyensOutils={changerMoyensOutils}
        />
        <SectionDevis
          phases={formData.commun.phases}
          fraisDossier={formData.commun.fraisDossier}
          onChangerFraisDossier={changerFraisDossier}
          onModifierPhase={modifierPhase}
        />
      </div>

      <div id="paiement">
        <SectionPaiement
          phases={formData.commun.phases}
          fraisDossier={formData.commun.fraisDossier}
          conditionsPaiement={formData.commun.conditionsPaiement}
          onChangerConditionsPaiement={changerConditionsPaiement}
        />
      </div>

      {/* ── Section spécifique Devis (nombreIntervenants vient de commun,
          car réutilisé par la Convention d'Étude ; dateValidite reste propre
          au Devis) ── */}
      <div id="devis-specifique">
        <SectionDevisSpecifique
          donnees={{
            dateValidite: formData.documents.devis.dateValidite,
            nombreIntervenants: formData.commun.nombreIntervenants,
          }}
          onChange={(cle, valeur) =>
            cle === 'nombreIntervenants'
              ? changerChampCommun('nombreIntervenants', valeur)
              : changerChampDocument('devis', cle, valeur)
          }
        />
      </div>

      {/* ── Phases et livrables réalisés : une seule section, partagée entre
          PVRI et PVRF. Coché au fur et à mesure de l'avancement de l'étude ── */}
      <div id="phases-livrables-realises">
        <SectionSelectionPhasesLivrables
          phases={formData.commun.phases}
          livrables={formData.commun.livrables}
          phasesConcerneesIds={formData.commun.phasesRealiseesIds}
          livrablesRemisIds={formData.commun.livrablesRemisIds}
          onTogglePhase={(id) => toggleElementDansListeCommun('phasesRealiseesIds', id)}
          onToggleLivrable={(id) => toggleElementDansListeCommun('livrablesRemisIds', id)}
        />
      </div>

      {/* ── Zone de génération : tout est déjà rempli une fois au-dessus ── */}
      <div id="generation">
        <Carte titre="Générer les documents" icone="⬇️">
          {erreurGeneration && (
            <p style={{ color: 'var(--rouge)', fontSize: 13, marginBottom: 12 }}>{erreurGeneration}</p>
          )}
          <div className="grille grille--2col">
            {DOCUMENTS.map((doc) => {
              const donneesDoc = formData.documents[doc.id];
              const statut = badgeStatut(donneesDoc.derniereGeneration);
              const enCours = generationEnCoursId === doc.id;
              return (
                <div
                  key={doc.id}
                  style={{ border: '0.5px solid var(--gris-bord)', borderRadius: 10, padding: '12px 16px' }}
                >
                  <div style={{ fontWeight: 500, marginBottom: 6 }}>{doc.label}</div>
                  <span
                    style={{
                      display: 'inline-block',
                      fontSize: 12,
                      padding: '2px 10px',
                      borderRadius: 6,
                      background: statut.bg,
                      color: statut.couleur,
                      marginBottom: 10,
                    }}
                  >
                    {statut.texte}
                  </span>
                  <div>
                    <button
                      className="btn btn--principal"
                      disabled={enCours}
                      onClick={() => handleGenerer(doc)}
                    >
                      {enCours ? '⏳ Génération...' : '⬇️ Générer le .docx'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </Carte>
      </div>
    </div>
  );
}

export default VuePageEtude;